/* eslint-disable react/prop-types */
import * as React from 'react';

import { useRequest, useUpdateEffect } from 'ahooks';
import { Badge, Button, Dropdown, Flex, Select } from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { getPaymentGateways } from 'services';
import type {
  PaymentGatewayType,
  PurchaseStatus,
  PurchaseSummary,
} from 'types';
import {
  adjustPageNumber,
  dateTimeFormat,
  generateSelectOptions,
  generateTableDataSource,
  localizePurchaseStatus,
  normalizeToString,
  numberFormat,
  PATHNAME,
  UNIT,
} from 'utils';

const { PRICE } = UNIT;
const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    PURCHASES: { UPDATE_URL: PURCHASES_UPDATE_URL },
    PAYMENT_GATEWAYS: {
      BASE_URL: PAYMENT_GATEWAYS_BASE_URL,
      UPDATE_URL: PAYMENT_GATEWAYS_UPDATE_URL,
    },
  },
} = PATHNAME;

const purchaseStatuses = [
  'Purchased',
  'Posted',
  'Delivered',
  'Cancelled',
] satisfies PurchaseStatus[];

type PurchasesTableProps = {
  loading: boolean;
  total: number;
  purchases: PurchaseSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleStatuses: (statuses?: PurchaseStatus[]) => void;
  handlePaymentGatewayType: (paymentGatewayType?: PaymentGatewayType) => void;
};

const PurchasesTable = React.memo<PurchasesTableProps>(
  ({
    loading,
    total,
    purchases,
    pageSize,
    pageNumber,
    handleStatuses,
    handlePageSize,
    handlePageNumber,
    handlePaymentGatewayType,
  }) => {
    const { data: paymentGatewaysData, loading: paymentGatewaysLoading } =
      useRequest(getPaymentGateways, {
        cacheKey: 'payment-gateways',
      });

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const { paymentGateways = [] } = paymentGatewaysData ?? {};

    const purchasesDataSource = generateTableDataSource(purchases, 'id');
    const paymentGatewaysSelectOptions = generateSelectOptions(
      paymentGateways,
      'id',
      'title',
      'type',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 1250, y: 630 }}
        columns={[
          {
            title: 'شناسه',
            dataIndex: 'trackingCode',
            key: 'trackingCode',
            width: '10%',
            ellipsis: true,
            render: (_, { id, trackingCode }) => (
              <AntdRouterLink
                ellipsis
                to={`${PURCHASES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                className='font-mono'
              >
                {trackingCode}
              </AntdRouterLink>
            ),
          },
          {
            title: 'گیرنده',
            dataIndex: 'recipientAddress',
            key: 'recipientAddress',
            ellipsis: true,
            render: (
              _,
              { recipientAddress: { firstName = '', lastName = '' } },
            ) => (
              <span>
                {firstName} {lastName}
              </span>
            ),
          },
          {
            title: 'قیمت',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            ellipsis: true,
            render: (_, { totalPrice }) => (
              <span>{numberFormat(totalPrice, undefined, ` ${PRICE}`)}</span>
            ),
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<PurchaseStatus[] | undefined>
                  showSearch
                  allowClear
                  mode='multiple'
                  maxTagCount='responsive'
                  className='w-72'
                  placeholder='وضعیت‌های مورد نظر را انتخاب کنید'
                  options={purchaseStatuses.map(type => {
                    const { label } = localizePurchaseStatus(type);
                    return {
                      label,
                      value: type,
                    };
                  })}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handleStatuses(value);
                  }}
                />
              </div>
            ),
            render: (_, { status }) => {
              const { label, color } = localizePurchaseStatus(status);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'درگاه پرداخت',
            dataIndex: 'transaction',
            key: 'transaction',
            width: 180,
            ellipsis: true,
            filterMultiple: false,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<PaymentGatewayType | undefined>
                  showSearch
                  allowClear
                  className='w-72'
                  placeholder='درگاه پرداخت مورد نظر را انتخاب کنید'
                  loading={paymentGatewaysLoading}
                  options={paymentGatewaysSelectOptions}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handlePaymentGatewayType(value);
                  }}
                />
              </div>
            ),
            render: (_, { transaction }) => {
              if (!transaction) return null;

              const {
                paymentGateway: { id, title },
              } = transaction;

              return (
                <AntdRouterExternalLink
                  ellipsis
                  href={`${PAYMENT_GATEWAYS_BASE_URL}${PAYMENT_GATEWAYS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                >
                  {title}
                </AntdRouterExternalLink>
              );
            },
          },
          {
            title: 'فاکتور کاغذی',
            dataIndex: 'paperInvoice',
            key: 'paperInvoice',
            width: 120,
            ellipsis: true,
            render: (_, { paperInvoice }) => (
              <Flex justify='center'>
                {paperInvoice ? (
                  <CheckCircleOutlinedIcon className='text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-red-500' />
                )}
              </Flex>
            ),
          },
          {
            title: 'تاریخ سفارش',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 170,
            ellipsis: true,
            render: (_, { createdDate }) => (
              <bdi>{dateTimeFormat(new Date(createdDate))}</bdi>
            ),
          },
          {
            title: 'تاریخ آخرین ویرایش',
            dataIndex: 'lastModifiedDate',
            key: 'lastModifiedDate',
            width: 170,
            ellipsis: true,
            render: (_, { lastModifiedDate }) => (
              <bdi>{dateTimeFormat(new Date(lastModifiedDate))}</bdi>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-purchase',
                          label: (
                            <AntdRouterLink
                              to={`${PURCHASES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>ویرایش سفارش</span>
                            </AntdRouterLink>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={purchasesDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
PurchasesTable.displayName = 'PurchasesTable';

export { PurchasesTable };
